import React, { useEffect, useRef, useState } from 'react';
import { Business, BusinessLocation, BusinessHours } from '../types';
import { ArrowRight, X } from 'lucide-react';

import { GOOGLE_MAPS_CONFIG } from '../config/googleMaps';
import { Loader } from "@googlemaps/js-api-loader";

const loader = new Loader({
    apiKey: GOOGLE_MAPS_CONFIG.apiKey,
    libraries: ["places", "marker"],
});


// Simple Add Business Form Component
const AddBusinessLocationModal: React.FC<{
    isOpen: boolean;
    onSubmit: (data: Partial<Business>) => void;
    onCancel: () => void;
    initial?: Partial<Business>;
    userType?: string;
}> = ({ isOpen, onSubmit, onCancel, initial, userType }) => {

    const defaultHours: BusinessHours = {
        monday: { open: '09:00', close: '17:00', is_closed: true },
        tuesday: { open: '09:00', close: '17:00', is_closed: true },
        wednesday: { open: '09:00', close: '17:00', is_closed: true },
        thursday: { open: '09:00', close: '17:00', is_closed: true },
        friday: { open: '09:00', close: '17:00', is_closed: true },
        saturday: { open: '09:00', close: '17:00', is_closed: true },
        sunday: { open: '09:00', close: '17:00', is_closed: true },
    };

    const normalizeHours = (hours: any): BusinessHours => {
        if (!hours) return defaultHours;
        if (Array.isArray(hours)) {
            return hours.reduce((acc: BusinessHours, h: any) => {
                const day = h.day_of_week || h.day || h.dayOfWeek;
                if (!day) return acc;
                acc[day] = {
                    open: h.open ?? h.open_time ?? '09:00',
                    close: h.close ?? h.close_time ?? '17:00',
                    is_closed: Boolean(h.closed ?? h.is_closed ?? false),
                };
                return acc;
            }, {} as BusinessHours);
        }
        if (hours && typeof hours === 'object') return hours as BusinessHours;
        return defaultHours;
    };

    const [formData, setFormData] = useState<Partial<Business>>({
        name: '',
        address: '',
        category: '',
        phone: '',
        website: '',
        description: '',
        priceRange: '$',
        interestCategories: [],
        tags: [],
        features: [],
        orgId: initial?.orgId,
        hours: normalizeHours(defaultHours),
        isActive: initial?.isActive ?? true,
    });

    const inputRef = useRef<HTMLInputElement | null>(null);



    useEffect(() => {
        if (isOpen) {
            setFormData(prev => ({
                ...prev,
                name: initial?.name ?? '',
                address: initial?.address ?? '',
                category: initial?.category ?? '',
                phone: initial?.phone ?? '',
                website: initial?.website ?? '',
                description: initial?.description ?? '',
                priceRange: initial?.priceRange ?? '$',
                orgId: initial?.orgId ?? prev.orgId,
                interestCategories: initial?.interestCategories ?? prev.interestCategories ?? [],
                tags: initial?.tags ?? prev.tags ?? [],
                features: initial?.features ?? prev.features ?? [],
                latitude: initial?.latitude ?? prev.latitude ?? 0,
                longitude: initial?.longitude ?? prev.longitude ?? 0,
                hours: normalizeHours(initial?.hours ?? defaultHours),
                isActive: initial?.isActive ?? true,
            }));




            let autocomplete: google.maps.places.Autocomplete;

            loader.load().then(() => {
                if (!inputRef.current) return;

                // Create Autocomplete on our input
                autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
                    fields: [
                        "place_id",
                        "formatted_address",
                        "geometry",
                        "address_components",
                    ],
                    types: ["address"],
                });

                // When user selects a place
                autocomplete.addListener("place_changed", () => {
                    const place = autocomplete.getPlace();

                    if (!place.geometry) {
                        console.warn("No geometry available for place");
                        return;
                    }
                    handleChange('address', place.formatted_address ?? '');
                    handleChange('latitude', place.geometry.location?.lat() ?? 0);
                    handleChange('longitude', place.geometry.location?.lng() ?? 0);


                    // console.log("Selected address:", place.formatted_address);
                    // console.log("Latitude:", place.geometry.location?.lat());
                    // console.log("Longitude:", place.geometry.location?.lng());
                });
            });

            console.log('formData after initialization:', formData.hours);

            return () => {
                if (autocomplete) {
                    google.maps.event.clearInstanceListeners(autocomplete);
                }
            };




        }
    }, [isOpen, initial]);


    if (!isOpen) return null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.name?.trim() || !formData.address?.trim()) {
            alert('Please fill in the required fields: Business Name, Address, and Category.');
            return;
        }
        // onSubmit(formData);
        const hoursObj = formData.hours || defaultHours;
        onSubmit({
            id: initial?.id,                 // keep id if editing
            name: formData.name?.trim() ?? '',
            address: formData.address?.trim() ?? '',
            category: formData.category ?? '',
            priceRange: formData.priceRange ?? '$',
            phone: formData.phone || undefined,
            website: formData.website || undefined,
            description: formData.description || undefined,
            interestCategories: formData.interestCategories ?? [],
            tags: formData.tags ?? [],
            features: formData.features ?? [],
            orgId: initial?.orgId ?? formData.orgId,  // carry org link
            latitude: formData.latitude ?? 0,
            longitude: formData.longitude ?? 0,
            hours: hoursObj,
            isActive: formData.isActive ?? true,
        });
    };

    const handleChange = (field: string, value: string | number | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleHourToggle = (day: string, checked: boolean) => {
        setFormData(prev => ({
            ...prev,
            hours: {
                ...(prev.hours || {}),
                [day]: {
                    ...(prev.hours?.[day] || { open: '09:00', close: '17:00', is_closed: false }),
                    is_closed: !checked,
                },
            },
        }));
    };

    const handleHourChange = (day: string, field: 'open' | 'close', value: string) => {
        setFormData(prev => ({
            ...prev,
            hours: {
                ...(prev.hours || {}),
                [day]: {
                    ...(prev.hours?.[day] || { open: '09:00', close: '17:00', is_closed: false }),
                    [field]: value,
                },
            },
        }));
    };

    const days = [
        { key: 'monday', label: 'Monday' },
        { key: 'tuesday', label: 'Tuesday' },
        { key: 'wednesday', label: 'Wednesday' },
        { key: 'thursday', label: 'Thursday' },
        { key: 'friday', label: 'Friday' },
        { key: 'saturday', label: 'Saturday' },
        { key: 'sunday', label: 'Sunday' },
    ];

    const renderForm = () => {
        return (<>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Place Name *
                    </label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Enter place name"
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Address *
                    </label>
                    <input
                        type="text"
                        ref={inputRef}
                        value={formData.address}
                        onChange={(e) => handleChange('address', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Enter business address"
                        required
                    />
                </div>
                {userType === 'user' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Category *
                        </label>
                        <select
                            value={formData.category}
                            onChange={(e) => handleChange('category', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            required
                        >
                            <option value="">Select a category</option>
                            <option value="Restaurant">Restaurant</option>
                            <option value="Coffee Shop">Coffee Shop</option>
                            <option value="Retail Store">Retail Store</option>
                            <option value="Service">Service</option>
                            <option value="Entertainment">Entertainment</option>
                            <option value="Health & Wellness">Health & Wellness</option>
                            <option value="Professional Services">Professional Services</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>)}

                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Phone
                        </label>
                        <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => handleChange('phone', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="(555) 123-4567"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Price Range
                        </label>
                        <select
                            value={formData.priceRange}
                            onChange={(e) => handleChange('priceRange', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                            <option value="$">$ (Budget-friendly)</option>
                            <option value="$$">$$ (Moderate)</option>
                            <option value="$$$">$$$ (Expensive)</option>
                            <option value="$$$$">$$$$ (Very Expensive)</option>
                        </select>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Website
                    </label>
                    <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => handleChange('website', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="https://www.example.com"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Brief description of the business..."
                    />
                </div>

                {userType !== 'user' && (
                    <>
                        <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                            <input
                                type="checkbox"
                                checked={formData.isActive ?? true}
                                onChange={(e) => handleChange('isActive', e.target.checked)}
                                className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <div>
                                <label className="font-medium text-gray-900 cursor-pointer text-sm">Active</label>
                                <p className="text-xs text-gray-600 mt-0.5">
                                    Set your business is active
                                </p>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Business Hours
                            </label>
                            <div className="space-y-3">
                                {days.map((day) => {
                                    const info = formData.hours?.[day.key] || { open: '09:00', close: '17:00', is_closed: true };
                                    const isOpen = !info.is_closed;
                                    return (
                                        <div key={day.key} className="md:flex justify-between items-center gap-3 space-y-2 md:space-y-0">
                                            <div>
                                                <label className="flex items-center gap-2 w-28">
                                                    <input
                                                        type="checkbox"
                                                        checked={isOpen}
                                                        onChange={(e) => handleHourToggle(day.key, e.target.checked)}
                                                    />
                                                    <span className="text-sm text-gray-700 capitalize">{day.label}</span>
                                                </label>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="time"
                                                    value={info.open}
                                                    onChange={(e) => handleHourChange(day.key, 'open', e.target.value)}
                                                    disabled={!isOpen}
                                                    className="px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                                />
                                                <span className="text-gray-500">to</span>
                                                <input
                                                    type="time"
                                                    value={info.close}
                                                    onChange={(e) => handleHourChange(day.key, 'close', e.target.value)}
                                                    disabled={!isOpen}
                                                    className={`px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
                                                />
                                            </div>
                                            {!isOpen && <span className="text-xs text-gray-500">(Closed)</span>}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}

                <div className="flex space-x-3 pt-4">
                    <button
                        type="submit"
                        className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium"
                    >{initial ? ('Update Place Information') : ('Add New Place')}
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                    >
                        Cancel
                    </button>
                </div>
            </form>

        </>)
    };

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-900">{initial ? ('Update Place Information') : ('Add New Place')}</h2>
                        <button
                            onClick={onCancel}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                        >
                            ✕
                        </button>
                    </div>
                    <div className="p-6">
                        {renderForm()}
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddBusinessLocationModal;

